// The questionnaire.
//
// ---------------------------------------------------------------------------
// Shape of the experience
// ---------------------------------------------------------------------------
//
// One SECTION per screen, not one question per screen and not all 93 at once.
//
//   * One question per screen would mean ninety-odd taps of "Next" and no
//     sense of progress.
//   * All of them at once is the Word document we are replacing.
//   * A section is a coherent chunk a client can finish in a sitting — "About
//     you", "Car and vehicle" — which also makes the progress bar mean
//     something.
//
// The client can move anywhere, any time. Forwards, backwards, or straight to
// a section chip. Nothing is gated behind finishing something else, because
// people do their tax in the order their paperwork happens to be in.
//
// ---------------------------------------------------------------------------
// Conditional visibility
// ---------------------------------------------------------------------------
//
// Recomputed on every render from the current answers, using the SAME engine
// the server uses (@myshoebox/shared). That shared engine is the whole reason
// a question cannot be visible here and counted as hidden by the exporter —
// which is a bug that previously lost client receipts.

import {
  type AnswerValue,
  type Question,
  computeProgress,
  outstandingRequired,
  visibleQuestions,
  visibleSections,
} from '@myshoebox/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AttachmentSummary, QuestionnaireBundle, UploadLimits } from '../api/client.js';
import { ApiError, api } from '../api/client.js';
import { saveStateLabel, useAutosave } from '../hooks/useAutosave.js';
import { QuestionField } from './QuestionField.js';
import { Review } from './Review.js';

export interface QuestionnaireProps {
  bundle: QuestionnaireBundle;
  limits: UploadLimits;
  onLeave: () => void;
  onSubmitted: () => void;
}

export function Questionnaire({
  bundle,
  limits,
  onLeave,
  onSubmitted,
}: QuestionnaireProps): JSX.Element {
  const year = bundle.questionnaire.year;
  const template = bundle.template;

  // Local answers are the source of truth while the client is working. The
  // server is told about changes, but we never wait for it to render.
  const [values, setValues] = useState<Record<string, AnswerValue>>(
    () => ({ ...bundle.values }) as Record<string, AnswerValue>,
  );
  const [prefilled, setPrefilled] = useState<Record<string, true>>(bundle.prefilled);
  const [attachments, setAttachments] = useState<AttachmentSummary[]>(bundle.attachments);

  const [sectionIndex, setSectionIndex] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: number; message: string; kind: string }>>([]);
  const [uploadingFor, setUploadingFor] = useState<string | undefined>(undefined);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);

  const mainRef = useRef<HTMLElement>(null);
  const toastId = useRef(0);

  const notify = useCallback((message: string, kind = 'info') => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts((current) => [...current, { id, message, kind }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 4500);
  }, []);

  // -------------------------------------------------------------------------
  // Autosave
  // -------------------------------------------------------------------------
  const {
    queue,
    flush,
    state: saveState,
    hasPending,
  } = useAutosave({
    save: async (patch) => {
      const result = await api.saveAnswers(year, patch);
      // The server rejects codes that are not in the template. That should
      // never happen from this UI, so it means a bug here — surface it in
      // development rather than letting answers quietly go nowhere.
      if (result.unknown.length > 0) {
        console.error('Server rejected unknown question codes:', result.unknown);
      }
    },
    beaconUrl: `/api/me/years/${year}/answers`,
  });

  const setAnswer = useCallback(
    (code: string, value: AnswerValue) => {
      setValues((current) => ({ ...current, [code]: value }));
      // The client has now looked at it, so it is their answer rather than
      // last year's suggestion.
      setPrefilled((current) => {
        if (!current[code]) return current;
        const next = { ...current };
        delete next[code];
        return next;
      });
      queue(code, value);
    },
    [queue],
  );

  // -------------------------------------------------------------------------
  // Visibility and progress, recomputed from the current answers
  // -------------------------------------------------------------------------
  const sections = useMemo(() => visibleSections(template, values), [template, values]);
  const progress = useMemo(() => computeProgress(template, values), [template, values]);
  const outstanding = useMemo(() => outstandingRequired(template, values), [template, values]);

  // An answer can hide the section the client is standing on — say they
  // change "do you have a rental?" to no while inside the rental section.
  // Clamp rather than crash or show an empty screen.
  const safeIndex = Math.min(sectionIndex, Math.max(0, sections.length - 1));
  const section = sections[safeIndex];

  useEffect(() => {
    if (safeIndex !== sectionIndex) setSectionIndex(safeIndex);
  }, [safeIndex, sectionIndex]);

  const goToSection = useCallback((index: number) => {
    setSectionIndex(index);
    setReviewing(false);
    // Back to the top, because the client is now looking at different
    // content and should not be halfway down it.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Move focus to the heading so a keyboard or screen-reader user is taken
    // to the new section rather than left where the old one was.
    requestAnimationFrame(() => mainRef.current?.focus());
  }, []);

  // -------------------------------------------------------------------------
  // Uploads
  // -------------------------------------------------------------------------
  const upload = useCallback(
    async (questionCode: string | null, files: File[]) => {
      const key = questionCode ?? '__general__';
      setUploadingFor(key);
      setUploadProgress(0);

      try {
        const result = await api.uploadAttachments(year, questionCode, files, (fraction) =>
          setUploadProgress(fraction),
        );

        if (result.saved.length > 0) {
          setAttachments((current) => {
            // Dedupe: the server returns the EXISTING row for an identical
            // re-upload, so a naive append would show it twice.
            const byId = new Map(current.map((a) => [a.id, a]));
            for (const saved of result.saved) byId.set(saved.id, saved);
            return [...byId.values()];
          });

          const deduped = result.saved.filter((s) => (s as { deduped?: boolean }).deduped).length;
          if (deduped > 0 && deduped === result.saved.length) {
            notify('You had already added that file.', 'info');
          } else {
            notify(
              `${result.saved.length} file${result.saved.length > 1 ? 's' : ''} attached.`,
              'success',
            );
          }
        }

        // Each rejection gets its own message. The server's reasons are
        // written for a client to read, so they are shown verbatim.
        for (const rejected of result.rejected) {
          notify(`${rejected.name}: ${rejected.error}`, 'error');
        }
      } catch (error) {
        notify(
          error instanceof ApiError ? error.message : 'Could not upload that. Please try again.',
          'error',
        );
      } finally {
        setUploadingFor(undefined);
        setUploadProgress(undefined);
      }
    },
    [year, notify],
  );

  const removeAttachment = useCallback(
    async (id: string) => {
      // Optimistic: the file disappears immediately and comes back if the
      // delete fails. Waiting on the network to remove something feels broken.
      const previous = attachments;
      setAttachments((current) => current.filter((a) => a.id !== id));
      try {
        await api.deleteAttachment(id);
      } catch (error) {
        setAttachments(previous);
        notify(error instanceof ApiError ? error.message : 'Could not remove that file.', 'error');
      }
    },
    [attachments, notify],
  );

  // -------------------------------------------------------------------------
  // Keyboard navigation, for the desktop client
  // -------------------------------------------------------------------------
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      // Never hijack arrow keys while someone is typing or in a picker.
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'ArrowRight' && safeIndex < sections.length - 1) {
        goToSection(safeIndex + 1);
      } else if (event.key === 'ArrowLeft' && safeIndex > 0) {
        goToSection(safeIndex - 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [safeIndex, sections.length, goToSection]);

  // -------------------------------------------------------------------------
  // Review and submit
  // -------------------------------------------------------------------------
  if (reviewing) {
    return (
      <Review
        year={year}
        progress={progress}
        outstanding={outstanding}
        attachmentCount={attachments.length}
        status={bundle.questionnaire.status}
        onBack={() => setReviewing(false)}
        onJumpTo={(sectionCode) => {
          const index = sections.findIndex((s) => s.code === sectionCode);
          if (index >= 0) goToSection(index);
        }}
        onSubmit={async () => {
          // Flush first: submitting with unsent answers would tell the
          // practice the form is ready while the last thing typed is still
          // sitting in a browser tab.
          await flush();
          await api.submit(year);
          onSubmitted();
        }}
      />
    );
  }

  if (!section) {
    return (
      <main className="container">
        <p>Loading…</p>
      </main>
    );
  }

  const shown = visibleQuestions(section, values);
  const isLastSection = safeIndex === sections.length - 1;

  return (
    <div className="questionnaire">
      {/* ------------------------------------------------------------------ */}
      {/* Header: progress and save state, always visible                    */}
      {/* ------------------------------------------------------------------ */}
      <header className="appbar">
        <button
          type="button"
          className="btn btn--ghost appbar__back"
          onClick={async () => {
            await flush();
            onLeave();
          }}
        >
          ‹ Years
        </button>

        <div className="appbar__progress">
          {/* The real <progress> element carries the semantics for assistive
              technology; it is visually hidden because it cannot be styled to
              match this design consistently across browsers. The div below is
              the decorative bar sighted users see, and is hidden from the
              accessibility tree so the value is not announced twice. */}
          <progress
            className="visually-hidden"
            max={100}
            value={progress.percent}
            aria-label="Progress through the questionnaire"
          />
          <div className="progress" aria-hidden="true">
            <div className="progress__fill" style={{ width: `${progress.percent}%` }} />
          </div>
          <span className="appbar__percent">{progress.percent}%</span>
        </div>

        {/* aria-live so a screen reader announces "Saved" without stealing focus. */}
        <span className={`savestate savestate--${saveState.status}`} aria-live="polite">
          {saveStateLabel(saveState)}
        </span>
      </header>

      {/* Section chips. Horizontally scrollable on a phone, wrapped on a
          desktop — the client can jump anywhere at any time. */}
      <nav className="sectionnav" aria-label="Sections">
        {sections.map((candidate, index) => {
          const sectionProgress = progress.sections.find((s) => s.code === candidate.code);
          const complete = sectionProgress?.complete ?? false;
          return (
            <button
              key={candidate.code}
              type="button"
              className={`chip ${index === safeIndex ? 'is-active' : ''} ${complete ? 'is-complete' : ''}`}
              aria-current={index === safeIndex ? 'step' : undefined}
              onClick={() => goToSection(index)}
            >
              {candidate.icon && <span aria-hidden="true">{candidate.icon}</span>}
              <span>{candidate.title}</span>
              {complete && (
                <span className="chip__tick" aria-label="complete">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* ------------------------------------------------------------------ */}
      {/* The section                                                        */}
      {/* ------------------------------------------------------------------ */}
      {/* tabIndex -1 so goToSection can move focus here without making it a
          tab stop in normal use. */}
      <main className="container" ref={mainRef} tabIndex={-1}>
        <div className="section-intro">
          {section.icon && (
            <span className="section-intro__icon" aria-hidden="true">
              {section.icon}
            </span>
          )}
          <h1>{section.title}</h1>
          {section.blurb && <p className="section-intro__blurb">{section.blurb}</p>}
        </div>

        {saveState.status === 'error' && (
          <p className="notice notice--error" role="alert">
            {saveState.message} Your answers are still here — do not close this page.
          </p>
        )}

        {shown.map((question: Question) => (
          <QuestionField
            key={question.code}
            question={question}
            value={values[question.code]}
            isPrefilled={Boolean(prefilled[question.code])}
            attachments={attachments.filter((a) => a.questionCode === question.code)}
            acceptedMimeTypes={limits.allowedMimeTypes}
            onChange={(value) => setAnswer(question.code, value)}
            onUpload={(files) => void upload(question.code, files)}
            onDeleteAttachment={(id) => void removeAttachment(id)}
            uploading={uploadingFor === question.code}
            uploadProgress={uploadingFor === question.code ? uploadProgress : undefined}
          />
        ))}

        {shown.length === 0 && (
          <p className="muted">
            Nothing to answer here based on what you have told us. Carry on to the next section.
          </p>
        )}
      </main>

      {/* ------------------------------------------------------------------ */}
      {/* Footer: the primary navigation on a phone                          */}
      {/* ------------------------------------------------------------------ */}
      <footer className="formbar">
        <button
          type="button"
          className="btn btn--secondary"
          disabled={safeIndex === 0}
          onClick={() => goToSection(safeIndex - 1)}
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={async () => {
            if (isLastSection) {
              await flush();
              setReviewing(true);
            } else {
              goToSection(safeIndex + 1);
            }
          }}
        >
          {isLastSection ? 'Review' : 'Next'}
        </button>
      </footer>

      {/* Toasts sit above the footer so they never cover the Next button. */}
      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.kind}`}>
            {toast.message}
          </div>
        ))}
      </div>

      {/* Mentioned only when there is genuinely something unsaved, so it
          carries weight when it does appear. */}
      {hasPending && saveState.status === 'retrying' && (
        <output className="offline-banner">
          No connection — we will save as soon as it comes back. Your answers are safe.
        </output>
      )}
    </div>
  );
}
